<template lang="pug">
div
  AppScrollableTable(:has-fixed-column="false")
    AppTable(
      :table-data="projects"
      :selectable="atLeastOneBatchOption"
      hoverable
      :loading="showTableLoading"
      @selected="selectedProjects"
    )
      template(#empty)
        h4.projects-empty.text-muted.text-left No projects

      AppTableColumn(
        v-slot="{ row }"
        header="Name"
      )
        .d-flex.align-items-center.gap-5
          a.d-flex.align-items-center.gap-5(
            :href="projectPath(row)"
            :class="{ 'dark-link': row.integration_icon_info.disabled }"
          )
            AppAvatarInitial.avatar-overlaps(
              size="medium"
              :text="row.name"
            )
            .project-name
              | {{ row.name }}
            img.integration-icon(
              v-if="row.integration_icon_info.path"
              :src="row.integration_icon_info.path"
              :v-tooltip="{ placement: 'auto bottom' }"
              :title="row.integration_icon_info.integration_name"
              :data-title="row.integration_icon_info.integration_name"
              :class="{ 'integration-icon-disabled': row.integration_icon_info.disabled }"
            )
          i.hi.hi-info.hi-18.text-warning.a-tooltip(
            v-if="row.integration_icon_info.disabled"
            v-tooltip="{ placement: 'auto bottom' }"
            :title="archivedInText(row)"
          )

      AppTableColumn(
        v-slot="{ row }"
        :visible="teamsVisible"
        header="Teams"
      )
        AppHoverLink(
          :enabled="row.permissions.can_manage_teams"
          title="Edit this project's teams"
          @click="openEditDialog(row.id, 'teams')"
        )
          template(v-if="row.teams_data && !row.teams_data.all_count")
            | None
          template(v-else)
            AppAvatarInitial.avatar-overlaps(
              v-if="row.teams_data"
              size="medium"
              :text="row.teams_data.first_team_name"
            )
            AppAvatarInitial.avatar-overlaps(
              v-if="row.teams_data && row.teams_data.all_count === 2"
              size="medium"
              :text="row.teams_data.last_team_name"
            )
            span.more-members-badge.light(v-if="row.teams_data && row.teams_data.all_count > 2")
              | +{{ row.teams_data.all_count - 1 }}

      AppTableColumn(
        v-slot="{ row }"
        header="Members"
      )
        AppHoverLink(
          :enabled="row.permissions.can_manage_project_members"
          title="Edit this project's members"
          @click="openEditDialog(row.id, 'members')"
        )
          template(v-if="row.members_data && row.members_data.top_member_avatars")
            .avatar-container
              AppAvatar.avatar-overlaps(
                v-for="(member, index) in row.members_data.top_member_avatars"
                :key="index"
                size="medium"
                :avatar="member.speck"
              )
            span.more-members-badge.light(v-if="row.members_data && row.members_data.member_count > 3")
              | +{{ row.members_data.member_count - 3 }}

      AppTableColumn(
        v-slot="{ row }"
        header="To-dos"
      )
        template(v-if="row.tasks_count")
          a.todos-number(
            v-if="isActiveProject(row)"
            :href="row.tasks_path"
            rel="noopener noreferrer"
            target="_blank"
          ) {{ row.tasks_count }}
          span.todos-number(v-else) {{ row.tasks_count }}
        template(v-else)
          small.text-lighter No to-dos

      AppTableColumn(
        v-slot="{ row }"
        header="Budget"
      )
        AppHoverLink(
          :enabled="isActiveProject(row) && row.permissions.can_manage_project_budget"
          title="Edit this project's budget"
          @click="openEditDialog(row.id, 'budget')"
        )
          template(v-if="row.budget_data && row.budget_data.can_manage")
            template(v-if="hasBudget(row.budget_data) && budgetsVisible")
              .budget-info.line-height-x2.a-tooltip.inline-block(
                v-tooltip="{ placement: 'bottom', html: true, container: 'body' }"
                :data-original-title="budgetProgressHint(row.budget_data)"
                data-tipclass="tooltip-max-w"
              )
                AppProgressBar(
                  :tracked="row.budget_data.spent"
                  :total="row.budget_data.amount"
                )
            template(v-else)
              small.text-lighter Budget:
              small.ml-5 none

      AppTableColumn(
        v-if="memberLimitsVisible"
        v-slot="{ row }"
        header="Member limits"
      )
        AppHoverLink(
          :enabled="isActiveProject(row) && row.permissions.can_manage_project_budget"
          :data-original-title="row.members_limits_set ? 'View or change member limits' : 'Add member limits'"
          @click="openEditDialog(row.id, 'budget', 'members')"
        )
          | {{ row.members_limits_set ? 'Set' : 'None' }}
      AppTableColumn(
        v-slot="{ row }"
        header=""
      )
        ProjectsTableActions(
          :project="row"
          @open-edit-dialog="openEditDialog"
          @open-delete-dialog="openDeleteDialog"
        )
  AppPagination(
    v-if="showPagination"
    :pagination="pagination"
    :resource="{ one: 'project', many: 'projects' }"
    @page-changed="onPageChanged"
  )
</template>

<script>
import {
  ACTIVE_PROJECT_STATUS,
  ARCHIVED_PROJECT_STATUS,
  DELETED_PROJECT_STATUS,
  BUDGET_TYPE_COST,
} from '~/app_constants';
import { ProjectsResource } from '~/resources';
import { mapGetters, mapState, mapActions } from 'vuex';
import { isNil } from 'lodash-es';
import ProjectsTableActions from './ProjectsTableActions.vue';
import { formatDuration } from '~/utils/time_utils';

export default {
  name: 'ProjectsTable',
  components: { ProjectsTableActions },
  data() {
    return {
      ACTIVE_PROJECT_STATUS,
      ARCHIVED_PROJECT_STATUS,
      DELETED_PROJECT_STATUS,
      BUDGET_TYPE_COST,
    };
  },
  computed: {
    ...mapState({
      organization: 'organization',
      projects: 'projects',
      pagination: 'pagination',
      status: 'status',
    }),
    ...mapGetters([
      'showTableLoading',
      'teamsVisible',
      'activeView',
      'budgetsVisible',
      'atLeastOneBatchOption',
      'memberLimitsVisible',
    ]),
    hasData() {
      return this.projects && this.projects.length > 0;
    },
    showPagination() {
      return this.pagination && this.hasData;
    },
  },
  methods: {
    ...mapActions(['setBatchActionIds', 'setFilterLoadTable']),
    openEditDialog(projectId, tab, toggle = null) {
      this.$emit('open-edit-dialog', projectId, tab, toggle);
    },
    openDeleteDialog(project) {
      this.$emit('open-delete-dialog', project);
    },
    budgetProgressHint(budget) {
      let formattedBudgetAmount;
      let formattedBudgetSpent;
      let formattedBudgetRemaining;
      const progress = budget.spent / Number(budget.amount);
      const budgetProgressPercent = parseInt(progress * 100);
      const budgetRemainingPercent = parseInt(100 - progress * 100);

      if (budget.type === BUDGET_TYPE_COST) {
        formattedBudgetAmount = budget.amount.formatMoney(this.organization.currency);
        formattedBudgetSpent = Number(budget.spent).formatMoney(this.organization.currency);
        formattedBudgetRemaining = Number(budget.remaining).formatMoney(this.organization.currency);
      } else {
        formattedBudgetAmount = formatDuration(budget.amount);
        formattedBudgetSpent = formatDuration(budget.spent);
        formattedBudgetRemaining = formatDuration(budget.remaining);
      }

      const spent = `<strong>Spent:</strong> ${formattedBudgetSpent}/${formattedBudgetAmount} <strong>(<em>${budgetProgressPercent}%</em>)</strong>`;
      const remaining = `<strong>Remaining:</strong> ${formattedBudgetRemaining} <strong>(<em>${budgetRemainingPercent}%</em>)</strong>`;

      return `<div class='text-left'>${spent}<br/>${remaining}</div>`;
    },
    hasBudget(budget) {
      return !isNil(budget.type) && !isNil(budget.amount);
    },
    projectPath(project) {
      return ProjectsResource.showLink({ id: project.id });
    },
    onPageChanged(page) {
      this.setFilterLoadTable({ page });
    },
    selectedProjects(selected) {
      const projectIds = selected.map(({ id }) => id);

      this.setBatchActionIds(projectIds);
    },
    isActiveProject(project) {
      return project.status === ACTIVE_PROJECT_STATUS;
    },
    archivedInText(row) {
      const inName = row.integration_icon_info.integration_name
        ? `in ${row.integration_icon_info.integration_name}`
        : '';

      return `This project was removed or archived ${inName} and is no longer linked`;
    },
  },
};
</script>
